import { useEffect, useRef, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import { Pilot3DLayer } from '../components/map/Pilot3DLayerFixed';
import { PilotData } from '../types';

interface UsePilot3DOptions {
  map: maplibregl.Map | null;
  pilots: Map<string, PilotData>;
  enabled: boolean;
  is3D: boolean;
}

export const usePilot3D = ({
  map,
  pilots,
  enabled,
  is3D
}: UsePilot3DOptions) => {
  const layerRef = useRef<Pilot3DLayer | null>(null);
  const layerAddedRef = useRef(false);

  // Initialize layer when 3D mode is enabled
  useEffect(() => {

    if (!map || !enabled || !is3D) {
      // Remove layer if conditions aren't met
      if (layerAddedRef.current && map) {
        try {
          if (map.getLayer('pilots-3d')) {
            map.removeLayer('pilots-3d');
          }
        } catch (e) {
          console.error('[usePilot3D] Error removing layer:', e);
        }
        layerAddedRef.current = false;
      }

      // Dispose of layer
      if (layerRef.current) {
        layerRef.current.dispose();
        layerRef.current = null;
      }

      return;
    }

    // Create and add layer if not already added
    if (!layerAddedRef.current) {

      // Create the layer
      layerRef.current = new Pilot3DLayer('pilots-3d');

      // Check if map is fully ready before adding the layer
      const addLayerWhenReady = () => {
        // Add to map
        try {
          const customLayer = layerRef.current!.getLayer();
          if (!map.getLayer('pilots-3d')) {
            map.addLayer(customLayer);
            layerAddedRef.current = true;

            // Initial pilot update - delay slightly to ensure renderer is ready
            if (pilots.size > 0) {
              setTimeout(() => {
                if (layerRef.current) {
                  layerRef.current.updatePilots(pilots);
                  // Force a repaint to ensure the layer renders
                  map.triggerRepaint();
                }
              }, 100);
            }
          }
        } catch (e) {
          console.error('[usePilot3D] Error adding layer:', e);
        }
      };

      // If map style is loaded, add immediately, otherwise wait for it
      if (map.isStyleLoaded()) {
        addLayerWhenReady();
      } else {
        map.once('style.load', addLayerWhenReady);
      }
    }
  }, [map, enabled, is3D, pilots]);

  // Update pilots whenever they change
  useEffect(() => {
    if (layerRef.current && layerAddedRef.current && enabled && is3D) {
      layerRef.current.updatePilots(pilots);
    }
  }, [pilots, enabled, is3D]);

  // Handle style changes (e.g., when switching map styles in 3D mode)
  useEffect(() => {
    if (!map || !layerRef.current || !enabled || !is3D) return;

    const handleStyleLoad = () => {
      if (layerAddedRef.current && layerRef.current) {

        // Add a delay to ensure the style is fully settled
        setTimeout(() => {
          // Re-add the layer after style change
          try {
            const customLayer = layerRef.current!.getLayer();
            if (!map.getLayer('pilots-3d')) {
              map.addLayer(customLayer);

              // Update pilots after re-adding with a slight delay
              if (pilots.size > 0) {
                setTimeout(() => {
                  if (layerRef.current) {
                    layerRef.current.updatePilots(pilots);
                    map.triggerRepaint();
                  }
                }, 100);
              }
            }
          } catch (e) {
            console.error('[usePilot3D] Error re-adding layer:', e);
          }
        }, 200); // Give style time to settle
      }
    };

    map.on('style.load', handleStyleLoad);

    return () => {
      map.off('style.load', handleStyleLoad);
    };
  }, [map, enabled, is3D, pilots]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (map && layerAddedRef.current) {
        try {
          if (map.getLayer('pilots-3d')) {
            map.removeLayer('pilots-3d');
          }
        } catch (e) {
          // Map might be destroyed
        }
      }

      if (layerRef.current) {
        layerRef.current.dispose();
        layerRef.current = null;
      }

      layerAddedRef.current = false;
    };
  }, [map]);

  // Return visibility control function
  const setVisibility = useCallback((visible: boolean) => {
    if (layerRef.current) {
      layerRef.current.setVisibility(visible);
    }
  }, []);

  return {
    setVisibility
  };
};